import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  ButtonGroup,
  Chip,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Close as CloseIcon,
  Share as ShareIcon,
  Sms as SmsIcon,
  Style as StyleIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  OpenInNew as OpenInNewIcon,
  BrandingWatermark as WatermarkIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { authFetch } from '../../utils/api';
import '../../styles/invoicePreview.css';

const InvoicePreviewModal = ({ show, onClose, invoice, mode = 'preview' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { currentSubscription, isPaidUser: contextIsPaid, getCurrentPlan, plans } = useSubscription();

  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewTheme, setPreviewTheme] = useState('classic');
  const [watermarkType, setWatermarkType] = useState('none');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const currentPlan = (typeof getCurrentPlan === 'function' ? getCurrentPlan() : currentSubscription?.plan) ||
    (plans && currentSubscription ? plans.find(p => p.id === (currentSubscription.plan_id || currentSubscription.planId)) : null);

  const isPaidUser = Boolean(
    contextIsPaid ||
    (
      currentSubscription &&
      (currentSubscription.status === 'active' || currentSubscription.is_lifetime) &&
      (
        currentSubscription.is_lifetime ||
        (currentPlan && (
          Number(currentPlan.price) > 0 ||
          currentPlan.name === '6 Months' ||
          currentPlan.name === '1 Month' ||
          currentPlan.name === '1 Year' ||
          currentPlan.name === '10 Years' ||
          currentPlan.name?.toLowerCase().includes('month') ||
          currentPlan.name?.toLowerCase().includes('year')
        )) ||
        (currentSubscription.plan_id && Number(currentSubscription.plan_id) > 2) ||
        (currentSubscription.planId && Number(currentSubscription.planId) > 2)
      )
    )
  );

  // Responsive zoom management
  const [fitScale, setFitScale] = useState(1);
  const [zoomMode, setZoomMode] = useState('fit'); // 'fit' | '100' | 'custom'
  const [customScale, setCustomScale] = useState(1);

  const containerRef = useRef(null);

  // Initialize theme and watermark when invoice changes
  useEffect(() => {
    if (invoice && invoice.theme) {
      setPreviewTheme(invoice.theme);
    } else {
      setPreviewTheme('classic');
    }

    if (invoice && (invoice.watermark || invoice.watermarkType)) {
      setWatermarkType(invoice.watermark || invoice.watermarkType);
    } else {
      setWatermarkType('none');
    }
  }, [invoice]);

  const handleWatermarkChange = (newVal) => {
    const diwaliLayouts = ['diwali_frame', 'diwali_diya', 'diwali_toran', 'diwali_rangoli'];
    if (diwaliLayouts.includes(newVal) && !isPaidUser) {
      setShowUpgradeModal(true);
      return;
    }
    setWatermarkType(newVal);
  };

  // Load PDF Blob
  const loadPdf = useCallback(async () => {
    if (!show || !invoice || !invoice.invoice_id) return;

    setLoadingPdf(true);
    setError(null);

    try {
      const timestamp = new Date().getTime();
      const res = await authFetch(
        `/invoices/${invoice.invoice_id}/pdf/preview?format=${previewTheme}&watermark=${watermarkType}&t=${timestamp}`
      );
      if (!res.ok) throw new Error('Failed to fetch PDF preview');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl((prevUrl) => {
        if (prevUrl) URL.revokeObjectURL(prevUrl);
        return url;
      });
    } catch (err) {
      console.error('Error fetching PDF preview:', err);
      setError(err.message || 'Error generating invoice preview');
    } finally {
      setLoadingPdf(false);
    }
  }, [show, invoice, previewTheme, watermarkType]);

  useEffect(() => {
    loadPdf();
  }, [loadPdf]);

  // Clean up Object URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Calculate dynamic fit scale based on actual container or viewport width
  const calculateFitScale = useCallback(() => {
    const a4WidthPx = 794; // Standard A4 width at 96 DPI
    let availableWidth = 0;

    if (containerRef.current) {
      const cw = containerRef.current.clientWidth || containerRef.current.offsetWidth;
      // Reserve small side padding (16px on mobile, 40px on desktop)
      const padding = isMobile ? 16 : 40;
      availableWidth = Math.max(120, cw - padding);
    } else if (typeof window !== 'undefined') {
      const padding = window.innerWidth < 600 ? 16 : 48;
      availableWidth = Math.max(120, window.innerWidth - padding);
    }

    if (availableWidth > 0) {
      // Allow scale up to 1.0 (or fit if smaller)
      const computed = Math.min(1.0, +(availableWidth / a4WidthPx).toFixed(3));
      setFitScale(computed);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!show) return;

    calculateFitScale();
    const t1 = setTimeout(calculateFitScale, 80);
    const t2 = setTimeout(calculateFitScale, 300);

    const handleResize = () => calculateFitScale();
    window.addEventListener('resize', handleResize);

    let observer;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      observer = new ResizeObserver(() => calculateFitScale());
      observer.observe(containerRef.current);
    }

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', handleResize);
      if (observer) observer.disconnect();
    };
  }, [show, calculateFitScale]);

  // Determine active effective scale
  const effectiveScale = zoomMode === 'fit' ? fitScale : (zoomMode === '100' ? 1.0 : customScale);

  const handleZoomIn = () => {
    const current = effectiveScale;
    const next = Math.min(2.0, +(current + 0.15).toFixed(2));
    setCustomScale(next);
    setZoomMode('custom');
  };

  const handleZoomOut = () => {
    const current = effectiveScale;
    const next = Math.max(0.3, +(current - 0.15).toFixed(2));
    setCustomScale(next);
    setZoomMode('custom');
  };

  const handleZoomFit = () => {
    setZoomMode('fit');
    calculateFitScale();
  };

  const handleZoom100 = () => {
    setZoomMode('100');
    setCustomScale(1.0);
  };

  const handleOpenFullscreen = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handleDownload = () => {
    if (!pdfUrl || !invoice) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `Invoice-${invoice.invoice_number || 'document'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleNativeShare = async () => {
    if (!invoice) return;

    setSending(true);
    try {
      const res = await authFetch(`/invoices/${invoice.invoice_id}/pdf?format=${previewTheme}&watermark=${watermarkType}`);
      if (!res.ok) throw new Error('Failed to fetch PDF');

      const blob = await res.blob();
      const file = new File([blob], `Invoice-${invoice.invoice_number}.pdf`, { type: 'application/pdf' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Invoice #${invoice.invoice_number}`,
          text: `Please find attached invoice #${invoice.invoice_number} from ${invoice.company_name || 'us'}. Total: ₹${invoice.total}`,
        });
      } else if (navigator.share) {
        await navigator.share({
          title: `Invoice #${invoice.invoice_number}`,
          text: `Invoice #${invoice.invoice_number} from ${invoice.company_name || 'us'}. Total: ₹${invoice.total}`,
        });
      } else {
        // Fallback for desktop/unsupported
        handleDownload();
        const subject = encodeURIComponent(`Invoice #${invoice.invoice_number} from ${invoice.company_name || ''}`);
        const body = encodeURIComponent(`Please find the invoice #${invoice.invoice_number} downloaded to your device.`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share failed:', err);
        setError('Failed to share invoice. Please try downloading instead.');
      }
    } finally {
      setSending(false);
    }
  };

  const handleNativeSMS = () => {
    if (!invoice) return;
    const mobile = invoice.bill_to_mobile || '';
    const message = `Hello ${invoice.bill_to_name || 'Customer'}, your invoice #${invoice.invoice_number} for Rs. ${invoice.total} is generated by ${invoice.company_name || 'us'}.`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`sms:${mobile}?body=${encodedMessage}`, '_self');
  };

  if (!show) return null;

  return (
    <>
      <Dialog
        open={show}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth={!isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100%' : '92vh',
          maxHeight: isMobile ? '100%' : '92vh',
          borderRadius: isMobile ? 0 : '12px',
          boxShadow: isMobile ? 'none' : '0 10px 40px rgba(0,0,0,0.25)',
          background: '#f8f9fa',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          m: isMobile ? 0 : 2
        }
      }}
    >
      <DialogTitle sx={{
        m: 0,
        p: { xs: 1.25, sm: 2 },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1, sm: 1.5 },
        borderBottom: '1px solid #e0e0e0',
        background: '#ffffff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        zIndex: 10
      }}>
        {/* Top Header Row */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="text.primary"
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {mode === 'share' ? 'Share Invoice' : 'Invoice Preview'}
            </Typography>
            {invoice?.invoice_number && (
              <Chip
                label={`#${invoice.invoice_number}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  height: 24,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  maxWidth: { xs: 120, sm: 200 }
                }}
              />
            )}
          </Box>

          {/* Quick Header Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            {/* Direct PDF Fullscreen Button */}
            <Tooltip title="Open PDF in Full Screen / Native Viewer">
              <span>
                <IconButton
                  size={isMobile ? "small" : "medium"}
                  color="primary"
                  onClick={handleOpenFullscreen}
                  disabled={!pdfUrl || loadingPdf}
                  sx={{
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.16)' },
                    p: { xs: 0.75, sm: 1 }
                  }}
                >
                  <OpenInNewIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </span>
            </Tooltip>

            {/* Share Button */}
            <Tooltip title="Share Invoice">
              <span>
                <IconButton
                  size={isMobile ? "small" : "medium"}
                  color="primary"
                  onClick={handleNativeShare}
                  disabled={sending || !invoice}
                  sx={{
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.16)' },
                    p: { xs: 0.75, sm: 1 }
                  }}
                >
                  <ShareIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </span>
            </Tooltip>

            {/* Download Button */}
            <Tooltip title="Download PDF">
              <span>
                <IconButton
                  size={isMobile ? "small" : "medium"}
                  color="primary"
                  onClick={handleDownload}
                  disabled={!pdfUrl || loadingPdf}
                  sx={{
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.16)' },
                    p: { xs: 0.75, sm: 1 }
                  }}
                >
                  <DownloadIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </span>
            </Tooltip>

            {/* Close Button */}
            <IconButton
              aria-label="close"
              onClick={onClose}
              size={isMobile ? "small" : "medium"}
              sx={{
                color: (theme) => theme.palette.grey[600],
                p: { xs: 0.75, sm: 1 },
                '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
              }}
            >
              <CloseIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Box>
        </Box>

        {/* Secondary Toolbar Row (Layout, Watermark, Zoom) */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          width: '100%',
          pt: 0.5
        }}>
          {/* Controls: Layout & Watermark */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: { xs: 1, sm: 'none' } }}>
            <FormControl size="small" sx={{ minWidth: { xs: 110, sm: 135 }, flex: { xs: 1, sm: 'none' } }}>
              <InputLabel id="theme-select-label" sx={{ fontSize: '0.85rem' }}>Layout</InputLabel>
              <Select
                labelId="theme-select-label"
                value={previewTheme}
                onChange={(e) => setPreviewTheme(e.target.value)}
                label="Layout"
                startAdornment={<StyleIcon sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary', fontSize: 16 }} />}
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#fff',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  height: 36
                }}
              >
                <MenuItem value="classic">Classic</MenuItem>
                <MenuItem value="modern">Modern</MenuItem>
                <MenuItem value="minimalist">Minimalist</MenuItem>
                <MenuItem value="professional">Professional</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 190 }, flex: { xs: 1, sm: 'none' } }}>
              <InputLabel id="watermark-select-label" sx={{ fontSize: '0.85rem' }}>Watermark / Frame</InputLabel>
              <Select
                labelId="watermark-select-label"
                value={watermarkType}
                onChange={(e) => handleWatermarkChange(e.target.value)}
                label="Watermark / Frame"
                startAdornment={<WatermarkIcon sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary', fontSize: 16 }} />}
                renderValue={(selected) => {
                  if (selected === 'diwali_frame') return 'Diwali Festive Frame';
                  if (selected === 'diwali_diya' || selected === 'diwali_wishes') return 'Diwali Wishes Watermark';
                  if (selected === 'diwali_toran') return 'Diwali Royal Toran';
                  if (selected === 'diwali_rangoli') return 'Diwali Shubh Labh Rangoli';
                  if (selected === 'center') return 'Center Large';
                  if (selected === 'stylized') return 'Elegant Stylized';
                  return 'None';
                }}
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#fff',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  height: 36
                }}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="center">Center Large</MenuItem>
                <MenuItem value="stylized">Elegant Stylized</MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  value="diwali_frame"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 0.8
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <span style={{ fontSize: '1.1rem' }}>🪔</span>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309', fontSize: '0.85rem' }}>
                        Diwali Festive Frame
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.7rem', display: 'block' }}>
                        Royal Gold Frame & Upright Diyas
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={isPaidUser ? "VIP" : "PRO"}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: isPaidUser ? '#dcfce7' : '#fef3c7',
                      color: isPaidUser ? '#166534' : '#b45309',
                      border: '1px solid',
                      borderColor: isPaidUser ? '#bbf7d0' : '#fde68a'
                    }}
                  />
                </MenuItem>
                {/* Temporarily commented out due to bug in center wishes watermark
                <MenuItem
                  value="diwali_diya"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 0.8
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <span style={{ fontSize: '1.1rem' }}>✨</span>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309', fontSize: '0.85rem' }}>
                        Diwali Wishes Watermark
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.7rem', display: 'block' }}>
                        Company Wishes Happy Diwali Highlight
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={isPaidUser ? "VIP" : "PRO"}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: isPaidUser ? '#dcfce7' : '#fef3c7',
                      color: isPaidUser ? '#166534' : '#b45309',
                      border: '1px solid',
                      borderColor: isPaidUser ? '#bbf7d0' : '#fde68a'
                    }}
                  />
                </MenuItem>
                */}
                <MenuItem
                  value="diwali_toran"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 0.8
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <span style={{ fontSize: '1.1rem' }}>🏮</span>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309', fontSize: '0.85rem' }}>
                        Diwali Royal Toran
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.7rem', display: 'block' }}>
                        Festive Toran & Hanging Diyas
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={isPaidUser ? "VIP" : "PRO"}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: isPaidUser ? '#dcfce7' : '#fef3c7',
                      color: isPaidUser ? '#166534' : '#b45309',
                      border: '1px solid',
                      borderColor: isPaidUser ? '#bbf7d0' : '#fde68a'
                    }}
                  />
                </MenuItem>
                <MenuItem
                  value="diwali_rangoli"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 0.8
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <span style={{ fontSize: '1.1rem' }}>𑁍</span>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309', fontSize: '0.85rem' }}>
                        Diwali Shubh Labh Rangoli
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.7rem', display: 'block' }}>
                        Auspicious Rangoli & Shubh-Labh
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={isPaidUser ? "VIP" : "PRO"}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: isPaidUser ? '#dcfce7' : '#fef3c7',
                      color: isPaidUser ? '#166534' : '#b45309',
                      border: '1px solid',
                      borderColor: isPaidUser ? '#bbf7d0' : '#fde68a'
                    }}
                  />
                </MenuItem>
              </Select>
            </FormControl>

            {mode === 'share' && (
              <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={<SmsIcon />}
                onClick={handleNativeSMS}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  height: 36,
                  px: 1.5,
                  fontSize: '0.8rem'
                }}
              >
                SMS
              </Button>
            )}
          </Box>

          {/* Zoom & View Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            <ButtonGroup size="small" variant="outlined" sx={{ bgcolor: '#fff', borderRadius: '8px' }}>
              <Tooltip title="Zoom Out">
                <span>
                  <Button
                    onClick={handleZoomOut}
                    sx={{ minWidth: 32, px: 0.75, height: 32 }}
                    disabled={effectiveScale <= 0.35}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Fit to Screen Width">
                <Button
                  onClick={handleZoomFit}
                  variant={zoomMode === 'fit' ? 'contained' : 'outlined'}
                  sx={{
                    fontSize: '0.75rem',
                    px: 1,
                    fontWeight: 600,
                    textTransform: 'none',
                    height: 32
                  }}
                >
                  Fit
                </Button>
              </Tooltip>
              <Tooltip title="100% Actual Size">
                <Button
                  onClick={handleZoom100}
                  variant={zoomMode === '100' ? 'contained' : 'outlined'}
                  sx={{
                    fontSize: '0.75rem',
                    px: 1,
                    fontWeight: 600,
                    textTransform: 'none',
                    height: 32
                  }}
                >
                  100%
                </Button>
              </Tooltip>
              <Tooltip title="Zoom In">
                <span>
                  <Button
                    onClick={handleZoomIn}
                    sx={{ minWidth: 32, px: 0.75, height: 32 }}
                    disabled={effectiveScale >= 1.9}
                  >
                    <ZoomInIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            </ButtonGroup>
          </Box>
        </Box>
      </DialogTitle>

      {/* Mobile Helper Notice */}
      {isMobile && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#e3f2fd',
          color: '#0d47a1',
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid #bbdefb',
          fontSize: '0.75rem',
          fontWeight: 500,
          gap: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <VisibilityIcon sx={{ fontSize: 16 }} />
            <span>Mobile Preview: {Math.round(effectiveScale * 100)}%</span>
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={handleOpenFullscreen}
            disabled={!pdfUrl}
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            sx={{ p: 0, minWidth: 'auto', fontSize: '0.75rem', textTransform: 'none', fontWeight: 600 }}
          >
            Full View
          </Button>
        </Box>
      )}

      {/* Main Preview Content */}
      <DialogContent
        ref={containerRef}
        sx={{
          p: { xs: 1, sm: 2 },
          backgroundColor: '#eef1f6',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: loadingPdf || error ? 'center' : 'flex-start',
          overflowX: 'auto',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          flex: 1,
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        {error ? (
          <Box sx={{
            p: 3,
            m: 2,
            bgcolor: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #ffcdd2',
            textAlign: 'center',
            maxWidth: 480,
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
          }}>
            <Typography variant="h6" fontWeight="bold" color="error.main" gutterBottom>
              Preview Unavailable
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={loadPdf}
                size="small"
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                Retry Preview
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                size="small"
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                Download PDF
              </Button>
            </Box>
          </Box>
        ) : loadingPdf ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
            gap: 2
          }}>
            <CircularProgress size={36} thickness={4} />
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Generating exact invoice preview...
            </Typography>
          </Box>
        ) : pdfUrl ? (
          <Box
            sx={{
              width: `${Math.round(794 * effectiveScale)}px`,
              height: `${Math.round(1123 * effectiveScale)}px`,
              position: 'relative',
              my: { xs: 1, sm: 2 },
              mx: 'auto',
              flexShrink: 0,
              borderRadius: previewTheme === 'modern' ? '12px' : '4px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
              backgroundColor: '#ffffff',
              overflow: 'hidden',
              transition: 'width 0.15s ease-out, height 0.15s ease-out'
            }}
          >
            <Box
              sx={{
                width: '794px',
                height: '1123px',
                transform: `scale(${effectiveScale})`,
                transformOrigin: 'top left',
                position: 'absolute',
                top: 0,
                left: 0
              }}
            >
              <iframe
                title="Invoice PDF Preview"
                src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                style={{
                  width: '794px',
                  height: '1123px',
                  border: 'none',
                  backgroundColor: '#ffffff',
                  display: 'block'
                }}
              />
            </Box>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>

    {/* Diwali Special PRO Feature Upgrade Modal */}
    <Dialog
      open={showUpgradeModal}
      onClose={() => setShowUpgradeModal(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: 1.5,
          textAlign: 'center',
          background: 'linear-gradient(145deg, #fffbeb 0%, #ffffff 100%)',
          border: '2px solid #f59e0b'
        }
      }}
    >
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Box sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: '#fef3c7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '32px',
          mx: 'auto',
          mb: 2,
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)'
        }}>
          🪔
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#92400e', mb: 1 }}>
          Diwali Festive Frame (PRO)
        </Typography>
        <Typography variant="body2" sx={{ color: '#4b5563', lineHeight: 1.6, mb: 2.5 }}>
          Diwali special borders, glowing corner diyas, and auspicious festival watermarks are exclusive to <strong>Hamara BillBook Paid Subscribers</strong>.
        </Typography>
        <Box sx={{
          bgcolor: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '10px',
          p: 1.5,
          mb: 2.5,
          textAlign: 'left'
        }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', mb: 0.5 }}>
            ⭐ What’s included:
          </Typography>
          <Typography variant="caption" sx={{ color: '#78350f', display: 'block' }}>
            • Ornate Diwali decorative page borders & corner diyas<br/>
            • Auspicious "शुभ दीपावली" sacred mandala watermark<br/>
            • Printable high-resolution festive PDFs for your clients
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setShowUpgradeModal(false);
              navigate('/subscription');
            }}
            sx={{
              bgcolor: '#d97706',
              color: '#fff',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '10px',
              py: 1.2,
              '&:hover': { bgcolor: '#b45309' }
            }}
          >
            Upgrade to Premium
          </Button>
          <Button
            variant="text"
            color="inherit"
            onClick={() => setShowUpgradeModal(false)}
            sx={{ textTransform: 'none', color: '#6b7280', fontSize: '0.85rem' }}
          >
            Maybe Later
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  </>
);
};

export default InvoicePreviewModal;
